// src/components/LeadForm.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import emailjs from "@emailjs/browser";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import {
  updateFormField,
  submitFormStart,
  submitFormSuccess,
  submitFormFailure,
} from "../store";

const LeadForm = () => {
  const dispatch = useDispatch();
  const { formData, isSubmitting } = useSelector((state) => state.leadForm);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (field) => (event) => {
    dispatch(updateFormField({ field, value: event.target.value }));
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (
      !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\D/g, ""))
    ) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.location?.trim()) {
      newErrors.location = "Address is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("1. Form submitted, validating...");

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      console.log("2. Validation errors:", validationErrors);
      setErrors(validationErrors);
      setSnackbar({
        open: true,
        message: "Please fix the errors in the form",
        severity: "error",
      });
      return;
    }

    console.log("3. No validation errors, setting submitting to true");
    dispatch(submitFormStart());

    try {
      console.log("4. Preparing to send email...");
      const SERVICE_ID = "service_a8t9sbt";
      const TEMPLATE_ID = "template_kkd9iri";
      const PUBLIC_KEY = "PinX8mgdGG_GoQx52";

      console.log("5. EmailJS credentials loaded");
      console.log("Service ID:", SERVICE_ID);
      console.log("Template ID:", TEMPLATE_ID);
      console.log("Public Key:", PUBLIC_KEY);

      const templateParams = {
        to: "info@decodia.in",
        to_name: "Decodia",
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone || "Not provided",
        message: formData.message || "No additional message provided",
        location: formData.location,
        date: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      if (typeof emailjs === "undefined") {
        throw new Error(
          "EmailJS library not loaded. Make sure you've added the script tag or imported the package.",
        );
      }

      if (typeof emailjs.send !== "function") {
        throw new Error(
          "emailjs.send is not a function. Check your EmailJS initialization.",
        );
      }

      const response = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY,
      );
      dispatch(submitFormSuccess());
      setSnackbar({
        open: true,
        message: "Thank you! Your request has been submitted successfully.",
        severity: "success",
      });

      // Reset form after successful submission
      setTimeout(() => {
        dispatch(updateFormField({ field: "name", value: "" }));
        dispatch(updateFormField({ field: "email", value: "" }));
        dispatch(updateFormField({ field: "phone", value: "" }));
        dispatch(updateFormField({ field: "message", value: "" }));
        setErrors({});
      }, 1500);
    } catch (error) {
      console.error("9. ❌ CATCH BLOCK - Error details:", {
        error,
        name: error.name,
        message: error.message,
        text: error.text,
        status: error.status,
        stack: error.stack,
      });

      dispatch(submitFormFailure(error.message));

      // Fallback: Open mail client
      const mailtoLink = `mailto:info@decodia.in?subject=Lead Form Submission: ${encodeURIComponent(
        formData.name,
      )}&body=Name: ${encodeURIComponent(formData.name)}%0D%0AEmail: ${encodeURIComponent(
        formData.email,
      )}%0D%0APhone: ${encodeURIComponent(
        formData.phone || "",
      )}%0D%0ALocation: ${encodeURIComponent(
        formData.location || "",
      )}%0D%0A%0D%0AProject Details:%0D%0A${encodeURIComponent(
        formData.message || "",
      )}`;

      setSnackbar({
        open: true,
        message: "Failed to submit. Would you like to send an email instead?",
        severity: "warning",
      });

      // Wait a moment then show confirmation for fallback
      setTimeout(() => {
        if (
          window.confirm(
            "Email service failed. Open your email client instead?",
          )
        ) {
          window.location.href = mailtoLink;
        }
      }, 500);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // TextField styling with visible text
  const textFieldStyles = {
    "& .MuiInputLabel-root": {
      color: "#ccc",
      "&.Mui-focused": {
        color: "#8B4513",
        fontWeight: 600,
      },
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#555",
        borderRadius: "4px",
      },
      "&:hover fieldset": {
        borderColor: "#777",
        borderWidth: "2px",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#8B4513",
        borderWidth: "2px",
      },
      "&.Mui-error fieldset": {
        borderColor: "#f44336",
        borderWidth: "2px",
      },
      "& .MuiInputBase-input": {
        color: "#333", // Dark text for visibility
        backgroundColor: "#fff", // White background for input
        padding: "16.5px 14px",
        borderRadius: "4px",
        "&::placeholder": {
          color: "#888",
          opacity: 1,
        },
      },
    },
    "& .MuiFormHelperText-root": {
      color: "#f44336",
      marginLeft: 0,
      fontSize: "0.75rem",
      fontWeight: 500,
    },
    marginBottom: "8px",
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ width: "100%" }}
      >
        <TextField
          fullWidth
          label="Full Name *"
          value={formData.name}
          onChange={handleChange("name")}
          margin="normal"
          required
          error={!!errors.name}
          helperText={errors.name}
          sx={textFieldStyles}
          InputProps={{
            sx: {
              "& input": {
                color: "#333",
                backgroundColor: "#fff",
              },
            },
          }}
        />

        <TextField
          fullWidth
          label="Email Address *"
          type="email"
          value={formData.email}
          onChange={handleChange("email")}
          margin="normal"
          required
          error={!!errors.email}
          helperText={errors.email}
          sx={textFieldStyles}
          InputProps={{
            sx: {
              "& input": {
                color: "#333",
                backgroundColor: "#fff",
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="Address"
          value={formData.location}
          onChange={handleChange("location")}
          margin="normal"
          error={!!errors.location}
          helperText={errors.location}
          placeholder="Enter Your Own Address"
          sx={textFieldStyles}
          InputProps={{
            sx: {
              "& input": {
                color: "#333",
                backgroundColor: "#fff",
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="Phone Number"
          value={formData.phone}
          onChange={handleChange("phone")}
          margin="normal"
          error={!!errors.phone}
          helperText={errors.phone}
          placeholder="+1 (555) 123-4567"
          sx={textFieldStyles}
          InputProps={{
            sx: {
              "& input": {
                color: "#333",
                backgroundColor: "#fff",
              },
            },
          }}
        />

        <TextField
          fullWidth
          label="Project Details"
          value={formData.message}
          onChange={handleChange("message")}
          margin="normal"
          multiline
          rows={4}
          placeholder="Tell us about your project..."
          sx={{
            "& input": {
              color: "#333",
              backgroundColor: "#fff",
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Send />
            )
          }
          sx={{
            mt: 3,
            py: 1.5,
            bgcolor: "#8B4513",
            color: "#fff",
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            borderRadius: "4px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            "&:hover": {
              bgcolor: "#654321",
              boxShadow: "0 6px 8px rgba(0, 0, 0, 0.15)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
            "&:disabled": {
              bgcolor: "#555",
              color: "#aaa",
              cursor: "not-allowed",
            },
            transition: "all 0.3s ease",
          }}
        >
          {isSubmitting ? "Submitting..." : "Book Free Consultation"}
        </Button>

        <Typography
          variant="body2"
          sx={{
            mt: 2,
            textAlign: "center",
            color: "#aaa",
            fontSize: "0.875rem",
            lineHeight: 1.5,
          }}
        >
          By submitting, you agree to our terms and privacy policy
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            maxWidth: "400px",
            bgcolor:
              snackbar.severity === "success"
                ? "#2e7d32"
                : snackbar.severity === "error"
                  ? "#d32f2f"
                  : snackbar.severity === "warning"
                    ? "#ed6c02"
                    : "#1976d2",
            color: "#fff",
            fontSize: "0.9rem",
            fontWeight: 500,
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            "& .MuiAlert-icon": {
              color: "#fff",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LeadForm;
